import React, { useState, useEffect } from 'react';
import { CountryData } from '../data/countryData';
import worldBankApi, { MacroeconomicIndicator } from '../services/worldBankApi';
import ImprovedSankeyDiagram from './ImprovedSankeyDiagram';
import EnhancedCharts from './EnhancedCharts';
import BarChartIcon from '@mui/icons-material/BarChart';
import { useNumberInput } from '../hooks/useNumberInput';
import './SalaryCalculatorPanel.css';

interface SalaryCalculatorPanelProps {
  country: CountryData | null;
  onClose: () => void;
  darkMode: boolean;
  onNavigateToCompanies?: (filters: { company?: string; location?: string }) => void;
  onNavigateToJobs?: (filters: { position?: string; location?: string; salaryMin?: number }) => void;
}

interface JobFilters {
  position: string;
  location: string;
  salaryMin: number;
  experience: string;
}

type EmployerCriteria = 'payment' | 'worklife' | 'rating';

const SalaryCalculatorPanel: React.FC<SalaryCalculatorPanelProps> = ({ 
  country, 
  onClose, 
  darkMode,
  onNavigateToCompanies,
  onNavigateToJobs 
}) => {
  const [calculations, setCalculations] = useState<any>(null);
  const [macroData, setMacroData] = useState<MacroeconomicIndicator | null>(null);
  const [employerCriteria, setEmployerCriteria] = useState<EmployerCriteria>('payment');
  const [loadingMacroData, setLoadingMacroData] = useState<boolean>(false);
  
  const salaryInput = useNumberInput({
    initialValue: country?.avgSalary || 0,
    min: 0,
    max: 10000000,
    step: 1000,
    allowEmpty: false,
    onValidChange: (value) => {
      // This will be called when a valid value is entered
    }
  });

  useEffect(() => {
    if (country) {
      // Set initial salary if field is empty
      if (!salaryInput.value || salaryInput.value === '0') {
        salaryInput.setValue(country.avgSalary.toString());
      }
      calculateTakeHome();
      loadMacroeconomicData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, salaryInput.value]);

  const loadMacroeconomicData = async () => {
    if (!country) return;
    
    setLoadingMacroData(true);
    try {
      // Get country code from mapping
      const countryCodeMapping = worldBankApi.getCountryCodeMapping();
      const countryCode = countryCodeMapping[country.id];
      
      if (countryCode) {
        const data = await worldBankApi.getCountryIndicators(countryCode);
        if (data) {
          setMacroData(data);
        }
      }
    } catch (error) {
      // Silently handle error - no console logging
    } finally {
      setLoadingMacroData(false);
    }
  };

  const calculateTakeHome = () => {
    if (!country) return;

    // Convert string to number for calculations
    const salaryNumber = parseFloat(salaryInput.value) || 0;

    // Calculate income tax
    let incomeTax = 0;
    let taxableIncome = salaryNumber;
    let previousBracket = 0;

    // Simple progressive tax calculation
    country.taxRates.income.forEach((rate, index) => {
      const bracketLimit = getBracketLimit(country.id, index);
      const taxableInBracket = Math.min(Math.max(taxableIncome - previousBracket, 0), bracketLimit - previousBracket);
      incomeTax += taxableInBracket * (rate / 100);
      previousBracket = bracketLimit;
    });

    // Calculate social security
    const socialSecurity = salaryNumber * (country.taxRates.social / 100);

    // Calculate net income
    const netIncome = salaryNumber - incomeTax - socialSecurity;

    // Calculate monthly amounts
    const monthlyNet = netIncome / 12;
    const monthlyGross = salaryNumber / 12;

    // Calculate living expenses
    const totalLivingCosts = Object.values(country.livingCosts).reduce((sum, cost) => sum + cost, 0);
    const monthlySavings = monthlyNet - totalLivingCosts;

    setCalculations({
      grossSalary: salaryNumber,
      incomeTax,
      socialSecurity,
      netIncome,
      monthlyGross,
      monthlyNet,
      totalLivingCosts,
      monthlySavings,
      savingsRate: (monthlySavings / monthlyNet) * 100,
      effectiveTaxRate: ((incomeTax + socialSecurity) / salaryNumber) * 100
    });
  };

  const getBracketLimit = (countryId: string, bracketIndex: number): number => {
    // Simplified bracket limits for demo
    const brackets: Record<string, number[]> = {
      usa: [10000, 40000, 85000, 163000, 207000, 518000],
      uk: [12500, 50000, 150000],
      germany: [10000, 60000, 280000],
      france: [10000, 26000, 74000, 160000],
      canada: [50000, 100000, 155000, 220000],
      australia: [18200, 45000, 120000, 180000],
      netherlands: [70000, 999999],
      switzerland: [100000, 999999],
      japan: [1950000, 3300000, 6950000, 9000000, 18000000, 40000000],
      singapore: [20000, 40000, 80000, 120000, 160000, 200000, 240000, 280000, 320000]
    };
    
    return brackets[countryId]?.[bracketIndex] || 999999;
  };

  // Navigate to job search with filters
  const navigateToJobSearch = (jobTitle: string) => {
    if (onNavigateToJobs) {
      // Use the callback to navigate within the app
      onNavigateToJobs({
        position: jobTitle,
        location: country?.name || ''
        // No salary filter - show all jobs for this position
      });
      onClose(); // Close the panel after navigation
    } else {
      // Fallback to URL navigation
      const searchParams = new URLSearchParams({
        q: jobTitle,
        location: country?.name || '',
        country: country?.id || ''
      });
      window.open(`/jobs?${searchParams.toString()}`, '_blank');
    }
  };

  // Navigate to company page with filters
  const navigateToCompany = (companyName: string) => {
    if (onNavigateToCompanies) {
      // Use the callback to navigate within the app
      onNavigateToCompanies({
        company: companyName,
        location: country?.name || ''
      });
      onClose(); // Close the panel after navigation
    } else {
      // Fallback to URL navigation
      const searchParams = new URLSearchParams({
        company: companyName,
        location: country?.name || '',
        country: country?.id || ''
      });
      window.open(`/companies?${searchParams.toString()}`, '_blank');
    }
  };

  // Get sorted employers based on criteria
  const getSortedEmployers = () => {
    if (!country) return [];
    
    // Create enhanced employer data with work-life and rating scores
    const enhancedEmployers = country.topEmployers.map(employer => ({
      ...employer,
      workLifeScore: Math.random() * 2 + 7, // 7-9 range (simulated)
      overallRating: Math.random() * 1.5 + 3.5, // 3.5-5 range (simulated)
      benefits: Math.random() * 2 + 7 // 7-9 range
    }));
    
    switch (employerCriteria) {
      case 'worklife':
        return enhancedEmployers.sort((a, b) => b.workLifeScore - a.workLifeScore);
      case 'rating':
        return enhancedEmployers.sort((a, b) => b.overallRating - a.overallRating);
      case 'payment':
      default:
        return enhancedEmployers.sort((a, b) => b.avgSalary - a.avgSalary);
    }
  };

  const getEmployerCriteriaLabel = () => {
    switch (employerCriteria) {
      case 'worklife': return 'Work-Life Balance';
      case 'rating': return 'Overall Rating';
      case 'payment': return 'Average Salary';
      default: return 'Average Salary';
    }
  };

  const getEmployerValue = (employer: any) => {
    switch (employerCriteria) {
      case 'worklife': return `${employer.workLifeScore.toFixed(1)}/10`;
      case 'rating': return `⭐ ${employer.overallRating.toFixed(1)}`;
      case 'payment': return formatCurrency(employer.avgSalary, country?.id || '');
      default: return formatCurrency(employer.avgSalary, country?.id || '');
    }
  };

  const formatCurrency = (amount: number, countryId: string): string => {
    const currencies: Record<string, string> = {
      usa: 'USD',
      uk: 'GBP',
      germany: 'EUR',
      france: 'EUR',
      canada: 'CAD',
      australia: 'AUD',
      netherlands: 'EUR',
      switzerland: 'CHF',
      japan: 'JPY',
      singapore: 'SGD'
    };

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencies[countryId] || 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (!country) return null;

  return (
    <div className={`salary-calculator-panel ${darkMode ? 'dark' : 'light'} ${country ? 'open' : ''}`}>
      <div className="panel-header">
        <div className="country-header">
          <span className="country-flag-large">{country.flag}</span>
          <h2>{country.name}</h2>
        </div>
        <button className="close-btn" onClick={onClose}>×</button>
      </div>

      <div className="panel-content">
        <div className="salary-input-section">
          <label htmlFor="gross-salary">Enter Your Gross Annual Salary</label>
          <div className={`salary-input-wrapper ${!salaryInput.isValid ? 'error' : ''}`}>
            <input
              id="gross-salary"
              type="number"
              value={salaryInput.value}
              onChange={salaryInput.onChange}
              onBlur={salaryInput.onBlur}
              className="salary-input"
              placeholder="Enter amount"
              min="0"
              step="1000"
            />
            {salaryInput.error && (
              <span className="input-error">{salaryInput.error}</span>
            )}
          </div>
          <div className="country-avg">
            Country Average: {formatCurrency(country.avgSalary, country.id)}
          </div>
        </div>

        {calculations && (
          <>
            <div className="calculations-summary">
              <div className="summary-card">
                <h3>Monthly Take-Home</h3>
                <div className="amount">{formatCurrency(calculations.monthlyNet, country.id)}</div>
                <div className="sub-text">After taxes and social security</div>
              </div>
              
              <div className="summary-card">
                <h3>Monthly Savings</h3>
                <div className={`amount ${calculations.monthlySavings < 0 ? 'negative' : 'positive'}`}>
                  {formatCurrency(calculations.monthlySavings, country.id)}
                </div>
                <div className="sub-text">
                  {calculations.savingsRate.toFixed(1)}% savings rate
                </div>
              </div>
              
              <div className="summary-card">
                <h3>Effective Tax Rate</h3>
                <div className="amount">{calculations.effectiveTaxRate.toFixed(1)}%</div>
                <div className="sub-text">Total deductions</div>
              </div>
            </div>

            <div className="sankey-section">
              <h3>Income Breakdown</h3>
              <ImprovedSankeyDiagram
                data={{
                  grossSalary: calculations.grossSalary,
                  incomeTax: calculations.incomeTax,
                  socialSecurity: calculations.socialSecurity,
                  netIncome: calculations.netIncome,
                  livingCosts: {
                    rent: country.livingCosts.rent * 12,
                    food: country.livingCosts.food * 12,
                    transport: country.livingCosts.transport * 12,
                    utilities: country.livingCosts.utilities * 12,
                    entertainment: country.livingCosts.entertainment * 12,
                    healthcare: country.livingCosts.healthcare * 12
                  },
                  savings: calculations.monthlySavings * 12
                }}
                darkMode={darkMode}
              />
            </div>

            <div className="enhanced-charts-section">
              <h3><BarChartIcon style={{ marginRight: '8px', verticalAlign: 'middle' }} /> Detailed Market Analysis</h3>
              <EnhancedCharts country={country} darkMode={darkMode} />
            </div>

            <div className="statistics-section">
              {/* World Bank Macroeconomic Data */}
              <div className="stat-group macro-data-section">
                <h3><span className="material-icons">public</span> Economic Indicators (World Bank 2022)</h3>
                {loadingMacroData ? (
                  <div className="macro-data-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading economic data...</p>
                  </div>
                ) : macroData ? (
                  <div className="macro-data-grid">
                    {macroData.gdpPerCapita && (
                      <div className="macro-item">
                        <span className="macro-label">GDP per capita</span>
                        <span className="macro-value">${(macroData.gdpPerCapita / 1000).toFixed(1)}k</span>
                      </div>
                    )}
                    {macroData.unemploymentRate && (
                      <div className="macro-item">
                        <span className="macro-label">Unemployment</span>
                        <span className="macro-value">{macroData.unemploymentRate.toFixed(1)}%</span>
                      </div>
                    )}
                    {macroData.inflationRate && (
                      <div className="macro-item">
                        <span className="macro-label">Inflation</span>
                        <span className="macro-value">{macroData.inflationRate > 0 ? '+' : ''}{macroData.inflationRate.toFixed(1)}%</span>
                      </div>
                    )}
                    {macroData.urbanPopulation && (
                      <div className="macro-item">
                        <span className="macro-label">Urban Population</span>
                        <span className="macro-value">{macroData.urbanPopulation.toFixed(1)}%</span>
                      </div>
                    )}
                    {macroData.internetUsers && (
                      <div className="macro-item">
                        <span className="macro-label">Internet Users</span>
                        <span className="macro-value">{macroData.internetUsers.toFixed(1)}%</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="macro-data-empty">
                    <p>Economic data not available for this country</p>
                  </div>
                )}
              </div>

              <div className="stat-group">
                <h3>Cost of Living Index: {country.costOfLivingIndex}</h3>
                <div className="living-costs-breakdown">
                  {Object.entries(country.livingCosts).map(([category, cost]) => (
                    <div key={category} className="cost-item">
                      <span className="cost-category">{category.charAt(0).toUpperCase() + category.slice(1)}</span>
                      <span className="cost-amount">{formatCurrency(cost, country.id)}/mo</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="stat-group">
                <h3><span className="material-icons">work</span> Top Paying Jobs <span className="clickable-hint">(Click to search)</span></h3>
                <div className="top-list">
                  {country.topPayingJobs.map((job, index) => (
                    <div 
                      key={index} 
                      className="top-item clickable-job" 
                      onClick={() => navigateToJobSearch(job.title)}
                      title={`Search for ${job.title} jobs in ${country.name}`}
                    >
                      <span className="rank">{index + 1}</span>
                      <span className="name">{job.title}</span>
                      <span className="value">{formatCurrency(job.avgSalary, country.id)}</span>
                      <span className="search-icon material-icons">search</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="stat-group">
                <div className="employer-header">
                  <h3><span className="material-icons">business</span> Top Employers</h3>
                  <div className="employer-criteria-controls">
                    <label className="criteria-label">Ranked by:</label>
                    <div className="criteria-slider-container">
                      <input
                        type="range"
                        min="0"
                        max="2"
                        value={employerCriteria === 'payment' ? 0 : employerCriteria === 'worklife' ? 1 : 2}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          setEmployerCriteria(value === 0 ? 'payment' : value === 1 ? 'worklife' : 'rating');
                        }}
                        className="criteria-slider"
                      />
                      <div className="criteria-labels">
                        <span className={employerCriteria === 'payment' ? 'active' : ''}><span className="material-icons">attach_money</span> Salary</span>
                        <span className={employerCriteria === 'worklife' ? 'active' : ''}><span className="material-icons">balance</span> Work-Life</span>
                        <span className={employerCriteria === 'rating' ? 'active' : ''}><span className="material-icons">star</span> Rating</span>
                      </div>
                    </div>
                    <div className="current-criteria">
                      Showing: {getEmployerCriteriaLabel()}
                    </div>
                  </div>
                </div>
                <div className="top-list">
                  {getSortedEmployers().map((employer, index) => (
                    <div 
                      key={index} 
                      className="top-item employer-item clickable-employer"
                      onClick={() => navigateToCompany(employer.name)}
                      title={`View ${employer.name} company profile`}
                    >
                      <span className="rank">{index + 1}</span>
                      <span className="name">{employer.name}</span>
                      <span className="value">{getEmployerValue(employer)}</span>
                      <span className="search-icon material-icons">business</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="stat-group">
                <h3>Tax Breakdown</h3>
                <div className="tax-info">
                  <div className="tax-item">
                    <span>Income Tax Brackets:</span>
                    <span>{country.taxRates.income.join('%, ')}%</span>
                  </div>
                  <div className="tax-item">
                    <span>Social Security:</span>
                    <span>{country.taxRates.social}%</span>
                  </div>
                  <div className="tax-item">
                    <span>VAT/Sales Tax:</span>
                    <span>{country.taxRates.vat}%</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SalaryCalculatorPanel;